import React from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { type FilterValues } from '@/components/FilterDrawer'
import { ResetIcon } from './Icons'
import { FilterBarSkeleton } from './TableSkeletons'
import type { EventSelectValue } from './event/EventSelect'

interface FilterBarProps {
  filters: FilterValues
  onRemoveFilter: (key: keyof FilterValues, id?: string) => void
  onResetFilters: () => void
  isLoading?: boolean
  roleOptions?: { id: string; name: string; title: string }[]
  categoryOptions?: { id: string; name: string }[]
  selectedEvent?: EventSelectValue | null
  sponsorOptions?: { id: string; name: string }[]
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onRemoveFilter,
  onResetFilters,
  roleOptions,
  isLoading = false,
  categoryOptions,
  selectedEvent = null,
  sponsorOptions,
}) => {
  // ✅ Helper function to format filter display text
  const formatFilterDisplay = (
    key: keyof FilterValues,
    value: string | string[]
  ) => {
    const labels: Record<keyof FilterValues, string> = {
      userRole: 'User Role',
      subscriptionType: 'Subscription Type',
      eventAttendance: 'Event Attendance',
      registrationEndDate: 'Registration End Date',
      registrationStartDate: 'Registration Start Date',
      status: 'Status',
      statusEvent: 'Status',
      category: 'Category',
      purchaseDate: 'Purchase Date',
      eventId: 'Event',
      placement: 'Placement',
      bySponsor: 'By Sponsor',
      adStatus: 'Ad Status',
    }

    let displayValue: string

    if (key === 'userRole' && Array.isArray(value)) {
      const roleNames = value
        .map(roleId => roleOptions?.find(r => r.id === roleId)?.title || roleId)
        .join(', ')
      displayValue = roleNames || 'No roles selected'
    } else if (key === 'bySponsor' && Array.isArray(value)) {
      const sponsorNames = value
        .map(
          sponsorId =>
            sponsorOptions?.find(s => s.id === sponsorId)?.name || sponsorId
        )
        .join(', ')
      displayValue = sponsorNames || 'No sponsors selected'
    } else if (
      (key === 'registrationStartDate' || key === 'registrationEndDate') &&
      typeof value === 'string' &&
      value
    ) {
      const date = new Date(value)
      displayValue = date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } else {
      displayValue = typeof value === 'string' ? value : String(value)
    }

    return (
      <>
        <span className="text-xs font-semibold text-ss-neutral-950">
          {labels[key]}
        </span>
        <span className="text-xs font-medium text-ss-neutral-600">
          : {displayValue}
        </span>
      </>
    )
  }

  // ✅ Collect active filters only if they have values
  const activeFilters = Object.entries(filters)
    .filter(([key, value]) => {
      // Exclude adStatus from active filters unless it is explicitly set and non-empty
      if (key === 'adStatus' || key === 'bySponsor') {
        return Array.isArray(value) && value.length > 0
      }
      if (key === 'userRole' || key === 'status' || key === 'category') {
        return Array.isArray(value) && value.length > 0
      }
      return value !== '' && value !== null && value !== undefined
    })
    // Remove adStatus from display unless explicitly set
    .filter(
      ([key, value]) =>
        (key !== 'adStatus' && key !== 'bySponsor') ||
        (Array.isArray(value) && value.length > 0)
    ) as [keyof FilterValues, string | string[]][]

  // ✅ Don't render if there are no active filters
  if (isLoading) return <FilterBarSkeleton />
  if (activeFilters.length === 0) return null

  return (
    <div
      className="flex items-center gap-2 px-4 lg:px-6 py-2 flex-wrap"
      aria-live="polite"
      aria-atomic="true"
      aria-label="Active filters bar"
    >
      {filters.eventId && selectedEvent && (
        <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-transparent border border-ss-neutral-950 rounded-full text-sm font-medium text-ss-neutral-950">
          <span>
            <span className="text-xs font-semibold text-ss-neutral-950">
              Event
            </span>
            <span className="text-xs font-medium text-ss-neutral-600">
              : {selectedEvent.name}
            </span>
          </span>
          <button
            onClick={() => onRemoveFilter('eventId')}
            className="flex items-center justify-center w-4 h-4 rounded-sm hover:bg-ss-action-gray-50 transition-colors"
            aria-label={`Remove ${selectedEvent.name} event filter`}
          >
            <X className="w-3 h-3 text-ss-neutral-600" />
          </button>
        </div>
      )}

      {/* Render userRole filters individually */}
      {Array.isArray(filters.userRole) &&
        filters.userRole.length > 0 &&
        filters.userRole.map(roleId => {
          const roleName =
            roleOptions?.find(r => r.id === roleId)?.title || roleId
          return (
            <div
              key={`role-${roleId}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-transparent border border-ss-neutral-950 rounded-full text-sm font-medium text-ss-neutral-950"
            >
              <span>
                <span className="text-xs font-semibold text-ss-neutral-950">
                  User Role
                </span>
                <span className="text-xs font-medium text-ss-neutral-600">
                  : {roleName}
                </span>
              </span>
              <button
                onClick={() => onRemoveFilter('userRole', roleId)}
                className="flex items-center justify-center w-4 h-4 rounded-sm hover:bg-ss-action-gray-50 transition-colors"
                aria-label={`Remove ${roleName} role filter`}
              >
                <X className="w-3 h-3 text-ss-neutral-600" />
              </button>
            </div>
          )
        })}

      {/* Render status filters individually */}
      {Array.isArray(filters.status) &&
        filters.status.length > 0 &&
        filters.status.map(statusId => {
          const statusName =
            statusId.charAt(0).toUpperCase() + statusId.slice(1)
          return (
            <div
              key={`status-${statusId}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-transparent border border-ss-neutral-950 rounded-full text-sm font-medium text-ss-neutral-950"
            >
              <span>
                <span className="text-xs font-semibold text-ss-neutral-950">
                  Status
                </span>
                <span className="text-xs font-medium text-ss-neutral-600">
                  : {statusName}
                </span>
              </span>
              <button
                onClick={() => onRemoveFilter('status', statusId)}
                className="flex items-center justify-center w-4 h-4 rounded-sm hover:bg-ss-action-gray-50 transition-colors"
                aria-label={`Remove ${statusName} status filter`}
              >
                <X className="w-3 h-3 text-ss-neutral-600" />
              </button>
            </div>
          )
        })}

      {/* Render category filters individually */}
      {Array.isArray(filters.category) &&
        filters.category.length > 0 &&
        filters.category.map(categoryId => {
          const categoryName = categoryOptions?.find(
            c => c.id === categoryId
          )?.name

          return (
            <div
              key={`category-${categoryId}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-transparent border border-ss-neutral-950 rounded-full text-sm font-medium text-ss-neutral-950"
            >
              <span>
                <span className="text-xs font-semibold text-ss-neutral-950">
                  Category
                </span>
                <span className="text-xs font-medium text-ss-neutral-600">
                  : {categoryName}
                </span>
              </span>
              <button
                onClick={() => onRemoveFilter('category', categoryId)}
                className="flex items-center justify-center w-4 h-4 rounded-sm hover:bg-ss-action-gray-50 transition-colors"
                aria-label={`Remove ${categoryName} category filter`}
              >
                <X className="w-3 h-3 text-ss-neutral-600" />
              </button>
            </div>
          )
        })}

      {/* Render bySponsor filters individually */}
      {Array.isArray(filters.bySponsor) &&
        filters.bySponsor.length > 0 &&
        filters.bySponsor.map(sponsorId => {
          const sponsorName =
            sponsorOptions?.find(s => s.id === sponsorId)?.name ||
            `Sponsor ${sponsorId}`

          return (
            <div
              key={`sponsor-${sponsorId}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-transparent border border-ss-neutral-950 rounded-full text-sm font-medium text-ss-neutral-950"
            >
              <span>
                <span className="text-xs font-semibold text-ss-neutral-950">
                  By Sponsor
                </span>
                <span className="text-xs font-medium text-ss-neutral-600">
                  : {sponsorName}
                </span>
              </span>
              <button
                onClick={() => onRemoveFilter('bySponsor', sponsorId)}
                className="flex items-center justify-center w-4 h-4 rounded-sm hover:bg-ss-action-gray-50 transition-colors"
                aria-label={`Remove ${sponsorName} sponsor filter`}
              >
                <X className="w-3 h-3 text-ss-neutral-600" />
              </button>
            </div>
          )
        })}

      {/* Render adStatus filters individually */}
      {Array.isArray(filters.adStatus) &&
        filters.adStatus.length > 0 &&
        filters.adStatus.map(statusId => {
          const statusName =
            statusId.charAt(0).toUpperCase() + statusId.slice(1)
          return (
            <div
              key={`ad-status-${statusId}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-transparent border border-ss-neutral-950 rounded-full text-sm font-medium text-ss-neutral-950"
            >
              <span>
                <span className="text-xs font-semibold text-ss-neutral-950">
                  Ad Status
                </span>
                <span className="text-xs font-medium text-ss-neutral-600">
                  : {statusName}
                </span>
              </span>
              <button
                onClick={() => onRemoveFilter('adStatus', statusId)}
                className="flex items-center justify-center w-4 h-4 rounded-sm hover:bg-ss-action-gray-50 transition-colors"
                aria-label={`Remove ${statusName} ad status filter`}
              >
                <X className="w-3 h-3 text-ss-neutral-600" />
              </button>
            </div>
          )
        })}

      {/* Render other filters (excluding arrays handled above) */}
      {activeFilters
        .filter(
          ([key]) =>
            key !== 'userRole' &&
            key !== 'status' &&
            key !== 'category' &&
            key !== 'eventId' &&
            key !== 'adStatus' &&
            key !== 'bySponsor'
        )
        .map(([key, value]) => (
          <div
            key={key}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-transparent border border-ss-neutral-950 rounded-full text-sm font-medium text-ss-neutral-950"
          >
            <span>{formatFilterDisplay(key, value)}</span>
            <button
              onClick={() => onRemoveFilter(key)}
              className="flex items-center justify-center w-4 h-4 rounded-sm hover:bg-ss-action-gray-50 transition-colors"
              aria-label={`Remove ${key} filter`}
            >
              <X className="w-3 h-3 text-ss-neutral-600" />
            </button>
          </div>
        ))}

      {/* Reset all button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onResetFilters}
        className="h-8 px-3 text-ss-blue-500 hover:bg-ss-blue-50 hover:text-ss-blue-600"
      >
        <ResetIcon />
        Reset All
      </Button>
    </div>
  )
}

export default FilterBar
